import { useEsCelular } from '@tallerya/ui';
import { useEffect, useId, useState, type ReactNode } from 'react';
import { AppBar } from './AppBar';
import { BarraDerecha } from './BarraDerecha';
import { MenuLateral } from './MenuLateral';

// El layout del admin, como el Figma («Menu - Select»): el menú a la
// izquierda, la barra a la derecha, y entre las dos el app bar arriba y el
// contenido debajo.
//
// En escritorio los laterales están siempre. En celular no caben: se vuelven
// cajones que tapan el contenido y se abren desde el app bar.

type Cajon = 'menu' | 'barra' | null;

export function Layout({ children }: { children: ReactNode }) {
  const esCelular = useEsCelular();
  const [abierto, setAbierto] = useState<Cajon>(null);
  const [eraCelular, setEraCelular] = useState(esCelular);
  const idMenu = useId();
  const idBarra = useId();

  // Solo un cajón a la vez, y ninguno fuera del celular: si se agranda la
  // ventana con uno abierto —girar el teléfono—, se cierra, y al volver a
  // achicarla no reaparece solo.
  if (eraCelular !== esCelular) {
    setEraCelular(esCelular);
    setAbierto(null);
  }
  const cajon = esCelular ? abierto : null;
  const alternar = (cual: Exclude<Cajon, null>) => setAbierto((a) => (a === cual ? null : cual));
  const cerrar = () => setAbierto(null);

  // Escape cierra el cajón: tapa toda la pantalla y quien navega con teclado no
  // tendría otra salida que volver a buscar el botón.
  useEffect(() => {
    if (!cajon) return;
    function alTeclear(evento: KeyboardEvent) {
      if (evento.key === 'Escape') setAbierto(null);
    }
    document.addEventListener('keydown', alTeclear);
    return () => document.removeEventListener('keydown', alTeclear);
  }, [cajon]);

  const menuVisible = !esCelular || cajon === 'menu';
  const barraVisible = !esCelular || cajon === 'barra';

  return (
    <div className="layout">
      <div className="layout__menu" hidden={!menuVisible}>
        <MenuLateral id={idMenu} alElegir={cerrar} />
      </div>

      <AppBar
        menu={{ id: idMenu, visible: menuVisible, alternar: () => alternar('menu') }}
        barra={{ id: idBarra, visible: barraVisible, alternar: () => alternar('barra') }}
      />

      <main className="layout__contenido">{children}</main>

      <div className="layout__barra" hidden={!barraVisible}>
        <BarraDerecha id={idBarra} />
      </div>

      {/* Tocar fuera del cajón lo cierra. Es un div y no un botón: para el
          teclado ya está Escape, y un botón invisible más solo estorba al
          tabulador. */}
      {cajon && <div className="layout__velo" onClick={cerrar} />}
    </div>
  );
}
